import { isEqual as deepEqual } from 'lodash-es';
import type { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, map, share } from 'rxjs/operators';
import { addElements } from 'state/utils/array';
import { assert } from 'utils/assert/base';
import type { IFrontLogger } from 'utils/loggers/FrontLogger';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

interface BaseQueueEpicFactoryState<T> {
  queue: readonly T[];
  handling: readonly T[];
}

export class BaseQueueEpicFactory<T> {
  public readonly itemsToHandle$: Observable<readonly T[]>;

  private name: string;
  private maxConnections: number = -1;
  private allowDuplicateItems: boolean = false;
  private logger: IFrontLogger;

  private _state: BehaviorSubject<BaseQueueEpicFactoryState<T>>;
  private get state(): BaseQueueEpicFactoryState<T> {
    return this._state.getValue();
  }
  private set state(value: BaseQueueEpicFactoryState<T>) {
    this._state.next(value);
  }

  constructor(name: string, maxConnections: number, allowDuplicateItems: boolean = false) {
    assert(maxConnections > 0, 'maxConnections must be larger than 0');

    this.name = name;
    this.maxConnections = maxConnections;
    this.allowDuplicateItems = allowDuplicateItems;
    this._state = new BehaviorSubject<BaseQueueEpicFactoryState<T>>({
      queue: [],
      handling: [],
    });
    this.logger = getFrontLogger();

    this.itemsToHandle$ = this._state.asObservable().pipe(
      distinctUntilChanged(() => false, <any>deepEqual), // FIXME type error
      map(state => {
        const availableConnections = this.getAvailableConnections(this.maxConnections, state.handling);

        if (availableConnections <= 0) {
          return [];
        }
        return this.getNextInQueue(state.queue, availableConnections);
      }),
      filter(items => items.length > 0),
      share(),
    );
  }

  private getAvailableConnections(maxConnections: number, handlingQueue: readonly T[]): number {
    const availableConnections = maxConnections - handlingQueue.length;
    return availableConnections < 0 ? 0 : availableConnections;
  }

  private getNextInQueue(queue: readonly T[], availableConnections: number): readonly T[] {
    if (availableConnections <= 0) {
      return [];
    }
    return queue.slice(0, availableConnections);
  }

  private logWarningsForDuplicateItems(items: readonly T[]): void {
    if (this.allowDuplicateItems) {
      return;
    }
    items.forEach(item => {
      if (this.state.queue.some(x => deepEqual(x, item))) {
        this.logger.warn(`WARN: The item ${item} is already in the queue awaiting to be handled in ${this.name}.`);
      }
      if (this.state.handling.some(x => deepEqual(x, item))) {
        this.logger.warn(`WARN: The item ${item} is already in the queue awaiting handle completion in ${this.name}.`);
      }
    });
  }

  public addToQueue(...items: T[]): void {
    this.logWarningsForDuplicateItems(items);
    this.state = {
      queue: addElements(this.state.queue, ...items),
      handling: this.state.handling,
    };
  }

  public removeFromQueue(...items: readonly T[]): void {
    this.state = {
      queue: this.removeElements(this.state.queue, ...items),
      handling: this.state.handling,
    };
  }

  public emptyQueue(): void {
    this.state = {
      queue: [],
      handling: this.state.handling,
    };
  }

  public startHandling(...items: T[]): void {
    this.state = {
      queue: this.removeElements(this.state.queue, ...items),
      handling: addElements(this.state.handling, ...items),
    };
  }

  public completeHandling(...items: T[]): void {
    this.state = {
      queue: this.state.queue,
      handling: this.removeElements(this.state.handling, ...items),
    };
  }

  private removeElements(array: readonly T[], ...elementsToRemove: readonly T[]): readonly T[] {
    if (!elementsToRemove || elementsToRemove.length === 0) {
      return array;
    }
    return array.filter(e => elementsToRemove.every(elementToRemove => !deepEqual(elementToRemove, e)));
  }
}
